import { useState } from "react";

import type { Administrator, AdminRoleName } from "../api/types";
import { ErrorNotice } from "../components/ui";
import { ROLE_ORDER, ROLE_SUMMARY, roleLabel } from "./roles";
import { useRoleChange } from "./useUsers";

/**
 * What one administrator may do — the four fixed roles, granted one at a time.
 *
 * ONE ROLE PER REQUEST, and each request carries the acting owner's password.
 * A "save all my changes" button would be one confirmation for several
 * privilege changes, and the one that mattered would be indistinguishable from
 * the ones that did not; a role change is also the thing the audit trail
 * records as a single event, so the interface makes the same shape.
 *
 * THE TARGET'S PASSWORD IS NEVER ASKED FOR. The owner confirms with their own,
 * which is what re-authentication means: proving who is at the keyboard, not
 * proving anything about the person being changed.
 *
 * AN ADMINISTRATOR CANNOT EDIT THEIR OWN ROLES, and the controls say so rather
 * than failing when pressed. The backend refuses it independently — this is the
 * explanation, not the rule.
 */
export function RolesDialog({
  administrator,
  isSelf,
  onClose,
}: {
  administrator: Administrator;
  isSelf: boolean;
  onClose: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [lastResult, setLastResult] = useState<string | null>(null);
  const change = useRoleChange();

  const held = new Set<AdminRoleName>(administrator.roles);
  const ready = currentPassword.length > 0;

  function apply(role: AdminRoleName, grant: boolean) {
    setLastResult(null);
    change.mutate(
      {
        adminId: administrator.admin_id,
        role,
        grant,
        currentPassword,
      },
      {
        onSuccess: (result) => {
          setLastResult(
            result.changed
              ? `${roleLabel(role)} ${grant ? "granted" : "removed"}. ` +
                  `${administrator.display_name} was signed out of ` +
                  `${result.sessions_revoked} session` +
                  `${result.sessions_revoked === 1 ? "" : "s"} and must sign in again.`
              : `${administrator.display_name} already had that. Nothing changed.`,
          );
        },
      },
    );
  }

  return (
    <div className="dialog-backdrop" role="presentation">
      <div
        className="dialog dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="roles-title"
      >
        <h2 className="dialog__title" id="roles-title">
          Roles — {administrator.display_name}
        </h2>
        <div className="dialog__body">
          <div className="stack" data-testid="roles-dialog">
            {isSelf ? (
              <div className="notice" role="status">
                You cannot change your own roles. Ask another owner to do it —
                that is what keeps a privilege change a two-person operation.
              </div>
            ) : (
              <div className="users__field">
                <label className="users__label" htmlFor="roles-confirm-password">
                  Your password
                </label>
                <input
                  id="roles-confirm-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                />
                <p className="users__hint">
                  Your own password confirms each change. Every grant or removal
                  signs {administrator.display_name} out of every session, so
                  they receive their new authority by signing in again.
                </p>
              </div>
            )}

            <ul className="users__roles">
              {ROLE_ORDER.map((role) => {
                const has = held.has(role);
                return (
                  <li key={role} className="users__role">
                    <div className="users__roleText">
                      <span className="users__roleName">
                        {roleLabel(role)}
                        {has ? <span className="badge">held</span> : null}
                      </span>
                      <span className="muted">{ROLE_SUMMARY[role]}</span>
                    </div>
                    <button
                      type="button"
                      className={has ? "btn btn--sm" : "btn btn--primary btn--sm"}
                      disabled={isSelf || !ready || change.isPending}
                      onClick={() => apply(role, !has)}
                    >
                      {has ? "Remove" : "Grant"}
                    </button>
                  </li>
                );
              })}
            </ul>

            {lastResult ? (
              <div className="notice" role="status">
                {lastResult}
              </div>
            ) : null}
            {change.error ? <ErrorNotice error={change.error} /> : null}
          </div>
        </div>
        <div className="dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={change.isPending}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
